import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { forwardRef, ForwardedRef, ReactElement, useImperativeHandle, useMemo, useRef, useEffect } from 'react';
import { CellClickedEvent, ColumnState, GridReadyEvent, RowClickedEvent, EditableCallbackParams, CellKeyDownEvent, ColDef, CellDoubleClickedEvent, PostSortRowsParams, RowHeightParams } from 'ag-grid-community';
import { createSaveColumnState, getRowStyle, onGridReady } from 'src/utils/grid/gridStyleUtils';
import { defaultAggridRowHeight, HandledProps } from 'src/components/common/common';
import { useGridCellSelection } from 'src/utils/grid/GridHandlerContent';
import { RaonColDef } from 'src/types/grid';

interface GridExcelProps<TData> extends Omit<AgGridReactProps<TData>, HandledProps | 'onCellClicked' | 'onCellKeyDown'> {
    columnStateKey?: string;
    setColumnState?: (state: ColumnState[]) => void;
    onGridReady?: (params: GridReadyEvent<TData>) => void;
    onRowClicked?: (event: RowClickedEvent<TData>) => void;
    onCellClicked?: (params: CellClickedEvent<TData>) => void;
    onCellKeyDown?: (params: CellKeyDownEvent<TData>) => void;
    onCellDoubleClicked?: (params: CellDoubleClickedEvent<TData>) => void;
    onPostSortRows?: (params: PostSortRowsParams<TData>) => void;
    onColumnMoved?: () => void;
    onColumnPinned?: () => void;
    onColumnVisible?: () => void;
    onColumnResized?: () => void;
    disableBuiltinCellSelection?: boolean;
    /** @deprecated 컬럼 정의의 pasteable 속성으로 대체됨 */
    pasteableFields?: string[];
}

function GridExcel<TData extends {} = any>(
    props: GridExcelProps<TData>,
    ref: ForwardedRef<AgGridReact<TData>>
): ReactElement {
    // Grid_Excel 전용 prop + 나머지는 AgGridReact에 그대로 전달(...restProps)
    const {
        columnStateKey,
        setColumnState,
        onGridReady: onGridReadyProp,
        onRowClicked: onRowClickedProp,
        onCellClicked: handleCellClickProp,
        onCellKeyDown: handleCellKeyDownProp,
        onCellDoubleClicked: handleCellDoubleClickProp,
        onPostSortRows: onPostSortRowsProp,
        onColumnMoved: onColumnMovedProp,
        onColumnPinned: onColumnPinnedProp,
        onColumnVisible: onColumnVisibleProp,
        onColumnResized: onColumnResizedProp,
        disableBuiltinCellSelection = false,
        pasteableFields: _deprecated,
        rowHeight = defaultAggridRowHeight, 
        ...restProps
    } = props;

    // 그리드 인스턴스 참조 — 부모 ref와 동기화(useImperativeHandle)
    const gridRef = useRef<AgGridReact<TData>>(null);

    /**
     * columnDefs 중 pasteable: true 인 컬럼의 field 목록
     * - Ctrl+V 붙여넣기 진입 조건(최소 1개 pasteable 컬럼 존재) 및 시작 셀이 pasteable일 때만 우리 paste 로직 태우기 등에 사용
     */
    const pasteableFields = useMemo(() => {
        if (!props.columnDefs) return [];
        return (props.columnDefs as RaonColDef<TData>[])
            .filter(col => col.pasteable === true && col.field)
            .map(col => col.field!);
    }, [props.columnDefs]);

    /**
     * 컬럼 정의에 suppressKeyboardEvent 주입
     * - 편집 중 Ctrl+V 시 브라우저 input으로 붙여넣기 되는 것을 막음(preventDefault)
     * - pasteable 컬럼은 아래 onCellKeyDown에서 클립보드 기반 붙여넣기 처리
     */
    const injectedColumnDefs = useMemo(() => {
        if (!props.columnDefs) return props.columnDefs;
        return (props.columnDefs as RaonColDef<TData>[]).map(col => {
            const hasSuppressKeyboard = !!col.suppressKeyboardEvent;
            return {
                ...col,
                suppressKeyboardEvent: (params: any) => {
                    const isCtrlV = (params.event.ctrlKey || params.event.metaKey) && params.event.key === 'v';
                    if (isCtrlV) {
                        // 편집 중/아님 모두 false → onCellKeyDown에서 처리
                        return false;
                    }
                    return hasSuppressKeyboard ? col.suppressKeyboardEvent!(params) : false;
                }
            };
        });
    }, [props.columnDefs]);

    /**
     * 셀 클릭/키다운으로 범위 선택 등 처리 — injectedColumnDefs 기반으로 colDef 가공(processedColDefs)
     */
    const {
        anchorCellRef,
        selectedCellRef,
        handleCellClick_targeting,
        handleCellKeyDown,
        handleCellMouseDown,
        handleCellMouseOver,
        clearRange,
        processedColDefs,
    } = useGridCellSelection({
        columnDefs: disableBuiltinCellSelection ? null : injectedColumnDefs,
    });

    useImperativeHandle(
        ref,
        () => {
            const inst = gridRef.current;
            if (!inst) return null as unknown as AgGridReact<TData>;
            return Object.assign(inst, {
                clearRange: () => {
                    const api = gridRef.current?.api;
                    if (api) clearRange(api);
                },
            }) as AgGridReact<TData>;
        },
        [clearRange]
    );

    /** 컬럼 이동/핀/숨김/리사이즈 시 localStorage 등에 컬럼 상태 저장 */
    const saveColumnState = columnStateKey && setColumnState
        ? createSaveColumnState(gridRef, columnStateKey, setColumnState)
        : undefined;

    /** 해당 셀(colDef + 이벤트 params)이 편집 가능한지 — editable 함수/불리언 반영 */
    const isCellEditable = (colDef: ColDef<TData>, params: CellClickedEvent<TData> | CellKeyDownEvent<TData>): boolean => {
        if (typeof colDef.editable === 'function') {
            const editableParams: EditableCallbackParams<TData> = {
                node: params.node,
                data: params.data,
                colDef: colDef,
                column: params.column,
                api: params.api,
                context: params.context,
            };
            return colDef.editable(editableParams);
        }
        return !!colDef.editable;
    };

    /** 그리드 준비 완료: 셀 선택용 context 주입, 컬럼 상태 복원, 사용자 onGridReady 호출 */
    const handleGridReady = (params: GridReadyEvent<TData>) => {
        if (!disableBuiltinCellSelection) {
            params.api.setGridOption('context', {
                ...restProps.context,
                cellSelectionRef: selectedCellRef,
            });
        }
        if (columnStateKey && setColumnState) {
            onGridReady(gridRef, columnStateKey, setColumnState);
        }
        if (onGridReadyProp) onGridReadyProp(params);
    };

    /**
     * 셀 클릭: 편집 가능하면 부모 handleCellClickProp, 아니면 내장 셀 타겟팅(범위 선택)
     */
    const onCellClicked = (params: CellClickedEvent<TData>) => {
        const isEditable = isCellEditable(params.colDef, params);
        if (handleCellClickProp && isEditable) {
            handleCellClickProp(params);
        } else if (!disableBuiltinCellSelection) {
            handleCellClick_targeting(params);
        }
    };

    /**
     * 클립보드 텍스트를 탭/줄 단위로 파싱해 그리드에 반영 (행 붙여넣기 전용)
     */
    const executePaste = (api: any, focusedCell: any, text: string) => {
        const pastedRows = text
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .trimEnd()
            .split('\n')
            .map((row: string) => row.split('\t'));

        const allColumns = api.getAllDisplayedColumns();
        if (!allColumns) return;

        const startRowIndex = focusedCell.rowIndex;
        const startColIndex = allColumns.findIndex((col: any) => col === focusedCell.column);
        //  여기 추가해서 실제 값 확인
        // console.log('=== PASTE DEBUG ===');
        // console.log('focusedCell.column colId:', focusedCell.column?.getColId?.());
        // console.log('startColIndex:', startColIndex);
        // console.log('allColumns colIds:', allColumns.map((c: any) => c.getColId()));
        // console.log('pastedRows:', text.split('\n').map(r => r.split('\t')));

        if (startColIndex === -1) {
            console.log('❌ startColIndex -1 → paste 실패');
            return;
        }

        pastedRows.forEach((rowData: string[], rowOffset: number) => {
            rowData.forEach((cellValue: string, colOffset: number) => {
                const targetRowIndex = startRowIndex + rowOffset;
                const targetColIndex = startColIndex + colOffset;
                if (targetColIndex >= allColumns.length) return;

                const rowNode = api.getDisplayedRowAtIndex(targetRowIndex);
                if (!rowNode || rowNode.rowPinned) return;

                const column = allColumns[targetColIndex];
                const colDef = column.getColDef() as RaonColDef<TData>;
                const targetField = colDef.field;
                if (!targetField) return;

                const isPasteable = colDef.pasteable === true;
                const isEditable = isPasteable || (
                    typeof colDef.editable === 'function'
                        ? colDef.editable({ node: rowNode, data: rowNode.data, colDef, column, api, context: api.getGridOption('context') } as any)
                        : !!colDef.editable
                );

                if (!isEditable) return;

                const cleanValue = typeof cellValue === 'string'
                    ? cellValue.replace(/,/g, '').trim()
                    : cellValue;

                let finalValue: any = cleanValue;
                if (colDef.valueParser) {
                    finalValue = (colDef.valueParser as Function)({
                        newValue: cleanValue,
                        oldValue: rowNode.data?.[targetField as unknown as keyof TData],
                        data: rowNode.data,
                        node: rowNode,
                        colDef,
                        column,
                        api,
                        context: api.getGridOption('context'),
                    });
                }

                rowNode.setDataValue(targetField, finalValue);
            });
        });
    };

	/**
     * 클립보드 데이터가 여러 행/열인지 확인
     */
		const isMultiCellData = (text: string): boolean => {
			const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trimEnd();
			const rows = normalized.split('\n');
			// 여러 행이거나 탭 구분(여러 열)이 있으면 true
			return rows.length > 1 || rows.some(r => r.includes('\t'));
		};

    /**
     * 키다운 처리
     * 클립보드가 여러 행/열 → 편집 중지 후 executePaste (행 붙여넣기)
     * 클립보드가 단일 값   → 편집 중이면 return (브라우저 네이티브 paste)
     *                          편집 중 아니면 executePaste (단일 셀 붙여넣기)
     */
    const onCellKeyDown = (params: CellKeyDownEvent<TData>) => {
        const event = params.event as KeyboardEvent;

        // Ctrl+C → editable 체크 전에 먼저 처리 (범위 복사가 편집 불가 셀에서도 동작)
        if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
            if (!disableBuiltinCellSelection) {
                handleCellKeyDown(params);
                return;
            }
        }

        if ((event.ctrlKey || event.metaKey) && event.key === 'v' && pasteableFields.length > 0) {
            const field = params.colDef.field;
            if (field && pasteableFields.includes(field)) {

                const api = params.api;
                const editingCells = api.getEditingCells();

                const anchor = anchorCellRef.current;
                const focusedCell = anchor
                    ? {
                        rowIndex: anchor.rowIndex,
                        column: params.api.getAllDisplayedColumns()
                            .find((col: any) => col.getColId() === anchor.colId) ?? params.column,
                    }
                    : {
                        rowIndex: params.node.rowIndex!,
                        column: params.column,
                    };

                navigator.clipboard.readText().then(text => {
                    if (!text) return;

                    if (isMultiCellData(text)) {
                        // 여러 행/열 → 무조건 행 붙여넣기
                        // 편집 중이면 편집 취소 후 처리
                        if (editingCells.length > 0) {
                            api.stopEditing(true); // true: 변경사항 취소
                        }
                        executePaste(api, focusedCell, text);
                    } else {
                        // 단일 값 → 편집 중이면 브라우저 네이티브에 맡김
                        if (editingCells.length > 0) {
                            return; // 브라우저가 커서 위치에 붙여넣기
                        }
                        executePaste(api, focusedCell, text);
                    }
                }).catch(() => {});

                // 비편집 상태에서만 preventDefault (편집 중엔 네이티브 허용)
                if (editingCells.length === 0) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                return;
            }
        }

        const isEditable = isCellEditable(params.colDef, params);
        if (handleCellKeyDownProp && isEditable) {
            handleCellKeyDownProp(params);
        } else if (!disableBuiltinCellSelection) {
            handleCellKeyDown(params);
        }
    };

    /** 컬럼 레이아웃 변경 시 상태 저장 + 부모 콜백 */
    const handleColumnMoved = () => { saveColumnState?.(); onColumnMovedProp?.(); };
    const handleColumnPinned = () => { saveColumnState?.(); onColumnPinnedProp?.(); };
    const handleColumnVisible = () => { saveColumnState?.(); onColumnVisibleProp?.(); };
    const handleColumnResized = () => { saveColumnState?.(); onColumnResizedProp?.(); };
	

    // [추가] 행 높이 계산 함수
    const getRowHeight = (params: RowHeightParams) => {
        return rowHeight;
    };

    useEffect(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.resetRowHeights();
        }
    }, [rowHeight]);

    return (
        <div className="flex-1 ag-theme-alpine w-full overflow-y-auto relative">
            <AgGridReact<TData>
                ref={gridRef}
                overlayNoRowsTemplate={'<span style="padding: 32px;">조회된 데이터가 없습니다.</span>'}
                {...restProps}
                enableRangeSelection={true}
                suppressClipboardPaste={true}
                columnDefs={disableBuiltinCellSelection ? injectedColumnDefs : processedColDefs}
                onCellClicked={onCellClicked}
                onCellMouseDown={disableBuiltinCellSelection ? undefined : handleCellMouseDown}
                onCellMouseOver={disableBuiltinCellSelection ? undefined : handleCellMouseOver}
                onCellKeyDown={onCellKeyDown}
                onCellDoubleClicked={handleCellDoubleClickProp}
                postSortRows={onPostSortRowsProp}
                onRowClicked={onRowClickedProp}
                animateRows={true}
                stopEditingWhenCellsLoseFocus={true}
                suppressScrollOnNewData={true}
                suppressColumnVirtualisation={true}
                suppressColumnMoveAnimation={true}
                getRowStyle={getRowStyle}
                onGridReady={handleGridReady}
                onColumnMoved={handleColumnMoved}
                onColumnPinned={handleColumnPinned}
                onColumnVisible={handleColumnVisible}
                onColumnResized={handleColumnResized}
				
                headerHeight={rowHeight}
                getRowHeight={getRowHeight}
            />
        </div>
    );
}

/** forwardRef로 ref 전달 가능한 그리드 컴포넌트 */
const Grid_Excel = forwardRef(GridExcel) as <TData extends {} = any>(
    props: GridExcelProps<TData> & { ref?: ForwardedRef<AgGridReact<TData>> }
) => ReactElement;

(Grid_Excel as React.FunctionComponent).displayName = "Grid_Excel";

export default Grid_Excel;
